import { useMemo, useState } from "react";
import { toast } from "sonner";
import {
  BookOpen, Search, Copy, CheckCircle2, Target, Trophy, MessageSquare, ShieldAlert,
  Compass, FileSignature, Scale, HelpCircle, Map, DollarSign, LayoutDashboard, Boxes, Gauge, Megaphone,
} from "lucide-react";
import { safeCopy } from "@/lib/api";
import {
  GUIDE_SECTIONS, SETUP_ACCOUNTS, ESCALATION_CHANNELS, ONBOARDING_CHECKLIST, MONEY_RULES, INCENTIVES,
  DASHBOARD_MODULES, STUDIO_SERVICES, MARKETPLACE_SERVICES, REBUTTALS, KPI_STANDARDS, SOURCE_VAULT,
  OUTREACH_SCRIPTS, DISCOVERY_QUESTIONS, CLOSING_STEPS, ETHICS_RULES, REP_FAQ,
} from "@/components/sales/salesGuideData";

const SECTION_ICONS = {
  start: Compass, program: DollarSign, dashboard: LayoutDashboard, services: Boxes,
  rebuttals: ShieldAlert, kpis: Gauge, sources: Map, scripts: Megaphone,
  discovery: Target, closing: FileSignature, ethics: Scale, faq: HelpCircle,
};

const cardCls = "border border-white/10 bg-[#050505]/60 p-4";
const h3Cls = "font-sans font-bold text-sm text-zinc-100 uppercase tracking-wide";
const subCls = "font-mono text-[9px] tracking-[0.25em] text-zinc-500 uppercase";
const bodyCls = "font-mono text-[11px] text-zinc-400 leading-relaxed";

function RuleCard({ title, detail, testid }) {
  return (
    <div data-testid={testid} className={cardCls}>
      <p className={h3Cls}>{title}</p>
      <p className={`${bodyCls} mt-1.5`}>{detail}</p>
    </div>
  );
}

export default function SalesGuideWiki({ link, initialSection = "start" }) {
  const [active, setActive] = useState(initialSection);
  const [query, setQuery] = useState("");

  const personalize = (text) => text.split("[YOUR_PERSONAL_LEAD_LINK]").join(link || "[YOUR_PERSONAL_LEAD_LINK]");

  const copyScript = async (script) => {
    (await safeCopy(personalize(script.body))) ? toast.success("Script copied — your link is already inside") : toast.error("Copy blocked — copy manually");
  };

  // Global search across every section
  const results = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (q.length < 2) return null;
    const hits = [];
    const add = (section, kind, title, detail) => {
      if (`${title} ${detail}`.toLowerCase().includes(q)) hits.push({ section, kind, title, detail });
    };
    SETUP_ACCOUNTS.forEach((r) => add("start", "setup", r.title, r.detail));
    MONEY_RULES.forEach((r) => add("program", "money", r.title, r.detail));
    INCENTIVES.forEach((r) => add("program", "bonus", r.title, r.detail));
    DASHBOARD_MODULES.forEach((r) => add("dashboard", "module", r.module, `${r.location} — ${r.fn}`));
    STUDIO_SERVICES.forEach((r) => add("services", "studio", r.vertical, `${r.engines} — ${r.deliverables}`));
    MARKETPLACE_SERVICES.forEach((r) => add("services", "marketplace", r.title, r.detail));
    REBUTTALS.forEach((r) => add("rebuttals", "rebuttal", r.objection, r.rebuttal));
    KPI_STANDARDS.forEach((r) => add("kpis", "kpi", r.metric, `Standard ${r.standard} // Gold ${r.gold}`));
    SOURCE_VAULT.forEach((r) => add("sources", "source", r.category, `${r.platforms} — ${r.method}`));
    OUTREACH_SCRIPTS.forEach((r) => add("scripts", "script", r.title, r.body));
    DISCOVERY_QUESTIONS.forEach((r) => add("discovery", "question", r.q, r.detail));
    CLOSING_STEPS.forEach((r) => add("closing", "step", r.phase, r.action));
    ETHICS_RULES.forEach((r) => add("ethics", "rule", r.title, r.detail));
    REP_FAQ.forEach((r) => add("faq", "faq", r.q, r.a));
    return hits;
  }, [query]);

  const sectionTitle = (id) => GUIDE_SECTIONS.find((s) => s.id === id)?.title || id;

  return (
    <div data-testid="sales-guide-wiki" className="grid grid-cols-1 lg:grid-cols-[260px_1fr] gap-5 items-start">
      {/* Sidebar */}
      <div className="border border-white/10 bg-black/40 lg:sticky lg:top-24">
        <div className="border-b border-white/10 px-4 py-3 font-mono text-[10px] tracking-[0.3em] text-nc-orange uppercase flex items-center gap-2">
          <BookOpen className="w-3.5 h-3.5" /> Master Guide v2
        </div>
        <div className="p-3 border-b border-white/10">
          <div className="relative">
            <Search className="w-3.5 h-3.5 absolute left-3 top-1/2 -translate-y-1/2 text-zinc-500" />
            <input
              data-testid="guide-search-input"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="Search the whole guide…"
              className="w-full bg-[#050505] border border-white/10 pl-9 pr-3 py-2 font-mono text-[11px] text-zinc-100 rounded-none focus:outline-none focus:ring-1 focus:ring-nc-orange focus:border-nc-orange"
            />
          </div>
        </div>
        <nav className="flex flex-col max-h-[60vh] overflow-y-auto">
          {GUIDE_SECTIONS.map((s) => {
            const Icon = SECTION_ICONS[s.id] || BookOpen;
            return (
              <button
                key={s.id}
                data-testid={`guide-nav-${s.id}`}
                onClick={() => { setActive(s.id); setQuery(""); }}
                className={`text-left px-4 py-2.5 border-l-2 transition-colors ${active === s.id && !results ? "border-nc-orange bg-nc-orange/10 text-zinc-100" : "border-transparent text-zinc-400 hover:bg-white/5"}`}
              >
                <span className="font-mono text-[10px] tracking-[0.15em] uppercase flex items-center gap-2">
                  <Icon className="w-3.5 h-3.5 shrink-0" /> <span className="text-zinc-600">{s.num}</span> {s.title}
                </span>
              </button>
            );
          })}
        </nav>
      </div>

      {/* Content */}
      <div className="flex flex-col gap-4 min-w-0">
        {results ? (
          <div data-testid="guide-search-results" className="flex flex-col gap-3">
            <p className={subCls}>{results.length} result{results.length === 1 ? "" : "s"} for “{query.trim()}”</p>
            {results.length === 0 && <p className={`${bodyCls} py-6 text-center`}>No matches — try “FiveM”, “commission”, “HLOD”, “Kickstarter”, “budget”…</p>}
            {results.map((r, i) => (
              <button key={i} data-testid={`guide-result-${i + 1}`} onClick={() => { setActive(r.section); setQuery(""); }} className={`${cardCls} text-left hover:border-nc-orange/40 transition-colors`}>
                <p className="font-mono text-[8px] tracking-[0.25em] text-nc-orange uppercase">{sectionTitle(r.section)} // {r.kind}</p>
                <p className={`${h3Cls} mt-1`}>{r.title}</p>
                <p className={`${bodyCls} mt-1 line-clamp-2`}>{r.detail}</p>
              </button>
            ))}
          </div>
        ) : (
          <>
            <div className="border border-nc-orange/30 bg-nc-orange/5 px-4 py-3">
              <p className="font-mono text-[10px] tracking-[0.3em] text-nc-orange uppercase flex items-center gap-2">
                <Trophy className="w-3.5 h-3.5" /> {GUIDE_SECTIONS.find((s) => s.id === active)?.num} // {sectionTitle(active)}
              </p>
              <p className="font-mono text-[10px] text-zinc-500 mt-1">{GUIDE_SECTIONS.find((s) => s.id === active)?.blurb}</p>
            </div>

            {active === "start" && (
              <div data-testid="guide-section-start" className="flex flex-col gap-3">
                <p className={subCls}>Mandatory contractor account setup — Specpixel does NOT issue corporate accounts</p>
                {SETUP_ACCOUNTS.map((r, i) => <RuleCard key={i} testid={`guide-setup-${i + 1}`} {...r} />)}
                <p className={`${subCls} mt-2`}>Management escalation & official channels</p>
                {ESCALATION_CHANNELS.map((r, i) => <RuleCard key={i} {...r} />)}
                <div className="border border-nc-green/30 bg-nc-green/5 p-4">
                  <p className="font-mono text-[9px] tracking-[0.25em] text-nc-green uppercase mb-2 flex items-center gap-2"><CheckCircle2 className="w-3.5 h-3.5" /> Onboarding checklist</p>
                  {ONBOARDING_CHECKLIST.map((c, i) => <p key={i} className={`${bodyCls} mt-1`}>{i + 1}. {c}</p>)}
                </div>
              </div>
            )}

            {active === "program" && (
              <div data-testid="guide-section-program" className="flex flex-col gap-3">
                <div className="grid grid-cols-3 gap-2 text-center">
                  <div className="border border-nc-orange/30 bg-nc-orange/5 px-2 py-3"><p className="font-sans font-bold text-lg text-nc-orange">10%</p><p className="font-mono text-[8px] text-zinc-500 uppercase tracking-[0.2em] mt-0.5">Direct Commission</p></div>
                  <div className="border border-white/10 px-2 py-3"><p className="font-sans font-bold text-lg text-zinc-300">20%</p><p className="font-mono text-[8px] text-zinc-500 uppercase tracking-[0.2em] mt-0.5">Platform Fee</p></div>
                  <div className="border border-nc-green/30 bg-nc-green/5 px-2 py-3"><p className="font-sans font-bold text-lg text-nc-green">Auto</p><p className="font-mono text-[8px] text-zinc-500 uppercase tracking-[0.2em] mt-0.5">Tracked Link</p></div>
                </div>
                {MONEY_RULES.map((r, i) => <RuleCard key={i} testid={`guide-money-${i + 1}`} {...r} />)}
                <p className={`${subCls} mt-2`}>Unlimited revenue bonus incentives</p>
                {INCENTIVES.map((r, i) => (
                  <div key={i} data-testid={`guide-incentive-${i + 1}`} className="border border-nc-orange/30 bg-nc-orange/5 p-4">
                    <p className={`${h3Cls} text-nc-orange`}>{r.title}</p>
                    <p className={`${bodyCls} mt-1.5`}>{r.detail}</p>
                  </div>
                ))}
              </div>
            )}

            {active === "dashboard" && (
              <div data-testid="guide-section-dashboard" className="flex flex-col gap-3">
                {DASHBOARD_MODULES.map((m, i) => (
                  <div key={i} data-testid={`guide-module-${i + 1}`} className={`${cardCls} grid grid-cols-1 sm:grid-cols-[180px_1fr] gap-2`}>
                    <div><p className={h3Cls}>{m.module}</p><p className="font-mono text-[9px] text-nc-blue uppercase tracking-[0.2em] mt-1">{m.location}</p></div>
                    <p className={bodyCls}>{m.fn}</p>
                  </div>
                ))}
              </div>
            )}

            {active === "services" && (
              <div data-testid="guide-section-services" className="flex flex-col gap-3">
                <p className={subCls}>1 // Company-run studio services (direct Specpixel outsourcing)</p>
                {STUDIO_SERVICES.map((s, i) => (
                  <div key={i} data-testid={`guide-service-${i + 1}`} className={cardCls}>
                    <div className="flex items-center justify-between gap-3 flex-wrap"><p className={h3Cls}>{s.vertical}</p><p className="font-mono text-[9px] text-nc-blue uppercase tracking-[0.2em]">{s.engines}</p></div>
                    <p className={`${bodyCls} mt-1.5`}>{s.deliverables}</p>
                  </div>
                ))}
                <p className={`${subCls} mt-2`}>2 // Specpixel Dev Marketplace — private “Fiverr for Game Devs”</p>
                {MARKETPLACE_SERVICES.map((r, i) => <RuleCard key={i} {...r} />)}
                <p className={bodyCls}>You earn 10% commission on BOTH studio services and marketplace deals.</p>
              </div>
            )}

            {active === "rebuttals" && (
              <div data-testid="guide-section-rebuttals" className="flex flex-col gap-3">
                <p className={subCls}>Technical objection handling — exact high-conversion rebuttals</p>
                {REBUTTALS.map((r, i) => (
                  <div key={i} data-testid={`guide-rebuttal-${i + 1}`} className={cardCls}>
                    <p className="font-sans font-bold text-sm text-red-400">“{r.objection}”</p>
                    <p className="font-mono text-[9px] text-zinc-500 uppercase tracking-[0.2em] mt-1">Underlying concern: {r.concern}</p>
                    <div className="border-l-2 border-nc-green pl-3 mt-3">
                      <p className="font-mono text-[11px] text-zinc-200 leading-relaxed">{r.rebuttal}</p>
                    </div>
                  </div>
                ))}
              </div>
            )}

            {active === "kpis" && (
              <div data-testid="guide-section-kpis" className="flex flex-col gap-3">
                <p className={subCls}>Daily & weekly activity standards</p>
                <div className="border border-white/10">
                  <div className="grid grid-cols-[1fr_110px_110px] border-b border-white/10 px-4 py-2.5 bg-white/5">
                    <span className={subCls}>Activity Metric</span>
                    <span className={`${subCls} text-center`}>Standard</span>
                    <span className="font-mono text-[9px] tracking-[0.25em] text-nc-orange uppercase text-center">Gold Tier</span>
                  </div>
                  {KPI_STANDARDS.map((k, i) => (
                    <div key={i} data-testid={`guide-kpi-${i + 1}`} className="grid grid-cols-[1fr_110px_110px] items-center border-b border-white/5 px-4 py-3">
                      <span className="font-mono text-[11px] text-zinc-200">{k.metric}</span>
                      <span className="font-mono text-[11px] text-zinc-400 text-center">{k.standard}</span>
                      <span className="font-mono text-[11px] text-nc-orange font-bold text-center">{k.gold}</span>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {active === "sources" && (
              <div data-testid="guide-section-sources" className="flex flex-col gap-3">
                <p className={subCls}>Massive high-yield lead directory — unlimited source vault</p>
                {SOURCE_VAULT.map((s, i) => (
                  <div key={i} data-testid={`guide-source-${i + 1}`} className={cardCls}>
                    <p className={h3Cls}>{s.category}</p>
                    <p className="font-mono text-[9px] text-nc-blue uppercase tracking-[0.15em] mt-1">{s.platforms}</p>
                    <p className={`${bodyCls} mt-2`}>{s.method}</p>
                  </div>
                ))}
              </div>
            )}

            {active === "scripts" && (
              <div data-testid="guide-section-scripts" className="flex flex-col gap-3">
                <div className="border border-nc-orange/30 bg-nc-orange/5 p-3">
                  <p className="font-mono text-[10px] text-zinc-300 leading-relaxed"><span className="text-nc-orange font-bold">CRITICAL:</span> Always direct prospects to specpixel.com via your Personal Lead Link so their chat locks to your dashboard. Your link is auto-inserted into every script below.</p>
                </div>
                {OUTREACH_SCRIPTS.map((s, i) => (
                  <div key={s.id} data-testid={`guide-script-${i + 1}`} className={cardCls}>
                    <div className="flex items-center justify-between gap-3 flex-wrap">
                      <div><p className={h3Cls}>{s.title}</p><p className="font-mono text-[9px] text-nc-blue uppercase tracking-[0.2em] mt-1">{s.channel}</p></div>
                      <button data-testid={`guide-script-copy-${i + 1}`} onClick={() => copyScript(s)} className="font-mono text-[9px] tracking-[0.2em] uppercase px-3 py-2 border border-nc-orange/40 text-nc-orange hover:bg-nc-orange/10 transition-colors flex items-center gap-1.5">
                        <Copy className="w-3 h-3" /> Copy script
                      </button>
                    </div>
                    <pre className="font-mono text-[11px] text-zinc-300 leading-relaxed whitespace-pre-wrap mt-3 border border-white/5 bg-black/40 p-3 max-h-72 overflow-y-auto">{personalize(s.body)}</pre>
                  </div>
                ))}
              </div>
            )}

            {active === "discovery" && (
              <div data-testid="guide-section-discovery" className="flex flex-col gap-3">
                <p className={subCls}>Ask these 4 qualifying questions in live chat BEFORE drafting a quote</p>
                {DISCOVERY_QUESTIONS.map((d, i) => (
                  <div key={i} data-testid={`guide-discovery-${i + 1}`} className={`${cardCls} flex gap-4`}>
                    <span className="font-sans font-bold text-xl text-nc-orange shrink-0">{String(i + 1).padStart(2, "0")}</span>
                    <div><p className={h3Cls}>{d.q}</p><p className={`${bodyCls} mt-1`}>“{d.detail}”</p></div>
                  </div>
                ))}
              </div>
            )}

            {active === "closing" && (
              <div data-testid="guide-section-closing" className="flex flex-col gap-3">
                {CLOSING_STEPS.map((s, i) => (
                  <div key={i} data-testid={`guide-closing-${i + 1}`} className={`${cardCls} flex gap-4`}>
                    <span className="font-sans font-bold text-xl text-nc-orange shrink-0">{s.step}</span>
                    <div><p className={h3Cls}>{s.phase}</p><p className={`${bodyCls} mt-1`}>{s.action}</p></div>
                  </div>
                ))}
              </div>
            )}

            {active === "ethics" && (
              <div data-testid="guide-section-ethics" className="flex flex-col gap-3">
                <p className={subCls}>Client hand-off & ethics compliance</p>
                {ETHICS_RULES.map((r, i) => <RuleCard key={i} testid={`guide-ethics-${i + 1}`} {...r} />)}
              </div>
            )}

            {active === "faq" && (
              <div data-testid="guide-section-faq" className="flex flex-col gap-3">
                {REP_FAQ.map((f, i) => (
                  <div key={i} data-testid={`guide-faq-${i + 1}`} className={cardCls}>
                    <p className={`${h3Cls} flex items-start gap-2`}><MessageSquare className="w-4 h-4 text-nc-orange shrink-0 mt-0.5" /> {f.q}</p>
                    <p className={`${bodyCls} mt-2`}>{f.a}</p>
                  </div>
                ))}
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
}
